import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import UniversalHeader from './UniversalHeader';

// Add consistent styling - MathCampus Teal Design System
const addConsistentStyles = () => {
  if (document.getElementById('practice-launcher-styles')) return;

  const style = document.createElement('style');
  style.id = 'practice-launcher-styles';
  style.textContent = `
    .font-poppins { font-family: 'Poppins', sans-serif; }
    .font-opensans { font-family: 'Open Sans', sans-serif; }
    .text-primary { color: var(--mc-primary, #1CB7C8); }
    .bg-primary { background-color: var(--mc-primary, #1CB7C8); }
    .text-accent { color: var(--mc-primary, #1CB7C8); }
    .bg-accent { background-color: var(--mc-primary, #1CB7C8); }
    .bg-bg-cream { background-color: var(--mc-gray-100, #F5F5F7); }
    .text-text-dark { color: var(--mc-charcoal, #3E3E4C); }
    .border-primary { border-color: var(--mc-primary, #1CB7C8); }
    .hover\\:bg-primary:hover { background-color: var(--mc-primary-dark, #159BA9); }
    .hover\\:text-primary:hover { color: var(--mc-primary, #1CB7C8); }
    .hover\\:bg-orange-600:hover { background-color: var(--mc-primary-dark, #159BA9); }
    .hover\\:bg-blue-800:hover { background-color: var(--mc-primary-dark, #159BA9); }
  `;
  document.head.appendChild(style);
};

const PracticeSessionLauncher = ({ onBack, onStartSession }) => {
  const { user, userProfile } = useAuth();
  const [selectedSubtopic, setSelectedSubtopic] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(15);
  const [subtopics, setSubtopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionsToday, setSessionsToday] = useState(0);
  const [maxSessions, setMaxSessions] = useState(5);
  const [recentSessions, setRecentSessions] = useState([]);
  const [resumableSession, setResumableSession] = useState(null);
  const [showAllSessions, setShowAllSessions] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Duration options with estimated question counts
  const durationOptions = [
    { 
      minutes: 15, 
      label: '15 Minutes', 
      questions: 15, 
      icon: '⚡',
      description: 'Quick practice session'
    },
    { 
      minutes: 30, 
      label: '30 Minutes', 
      questions: 30, 
      icon: '🎯',
      description: 'Standard practice session'
    },
    { 
      minutes: 60, 
      label: '1 Hour', 
      questions: 60, 
      icon: '🚀',
      description: 'Intensive practice session'
    }
  ];

  // Common math subtopics by grade level
  const subtopicsByGrade = {
    'Year 3': [
      'Addition', 'Subtraction', 'Basic Multiplication', 'Basic Division',
      'Counting & Place Value', 'Simple Fractions', 'Basic Geometry',
      'Time & Calendar', 'Money & Shopping', 'Measurement'
    ],
    'Year 4': [
      'Multiplication Tables', 'Division Facts', 'Fractions & Decimals',
      'Area & Perimeter', 'Data & Graphs', 'Patterns & Algebra',
      'Angles & Shapes', '2D & 3D Shapes', 'Time Calculation', 'Problem Solving'
    ],
    'Year 5': [
      'Advanced Multiplication', 'Long Division', 'Decimal Operations',
      'Fraction Operations', 'Percentages', 'Volume & Capacity',
      'Coordinate Geometry', 'Statistics', 'Probability', 'Number Theory'
    ],
    'Year 6': [
      'Ratio & Proportion', 'Advanced Decimals', 'Mixed Numbers',
      'Algebraic Thinking', 'Advanced Geometry', 'Data Interpretation',
      'Financial Mathematics', 'Measurement Conversion', 'Problem Solving', 'Logic & Reasoning'
    ],
    'Year 7': [
      'Integers & Rational Numbers', 'Algebraic Expressions', 'Linear Equations',
      'Coordinate Geometry', 'Statistical Analysis', 'Probability Theory',
      'Geometric Construction', 'Financial Literacy', 'Scientific Notation', 'Advanced Problem Solving'
    ]
  };

  useEffect(() => {
    addConsistentStyles();
    
    // Add Google Fonts
    if (!document.getElementById('practice-launcher-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'practice-launcher-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Open+Sans:wght@400;500;600&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
    
    const userGrade = userProfile?.grade || 'Year 4';
    setSubtopics(subtopicsByGrade[userGrade] || subtopicsByGrade['Year 4']);
    fetchDailySessionLimit();
    fetchTodaysSessions();
    fetchRecentSessions();
    fetchResumableSession();
  }, [userProfile?.grade]);

  const fetchDailySessionLimit = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/practice/config/daily-limit`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMaxSessions(data.max_sessions || 5);
      }
    } catch (err) {
      console.error('Error fetching session limit:', err);
    }
  };

  const fetchTodaysSessions = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/practice/sessions/today/${user?.id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSessionsToday(data.count || 0);
      }
    } catch (err) {
      console.error('Error fetching today\'s sessions:', err);
    }
  };

  const fetchRecentSessions = async (limit = 10) => {
    try {
      const response = await fetch(`${backendUrl}/api/practice/sessions/recent/${user?.id}?limit=${limit}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setRecentSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Error fetching recent sessions:', err);
    }
  };

  const fetchResumableSession = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/practice/resume-session/${user?.id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_resumable_session) {
          setResumableSession(data.session);
        } else if (data.error) {
          console.warn('Resume session API warning:', data.error);
        }
      } else {
        // Only log as warning, not error, since this is non-critical
        console.warn(`Resume session API returned ${response.status}, continuing without resume feature`);
      }
    } catch (err) {
      // Only log as warning since this is a non-critical feature
      console.warn('Resume session feature unavailable, continuing normally:', err.message);
    }
  };

  const handleResumeSession = async () => {
    if (!resumableSession) return;
    
    setLoading(true);
    
    try {
      // Prepare session data for resuming (reuse questions from session_data)
      const sessionData = {
        session: resumableSession,
        questions: resumableSession.session_data?.questions || []
      };
      
      // Call parent component to start the resumed session
      onStartSession(sessionData);
      
    } catch (err) {
      console.error('Error resuming practice session:', err);
      alert('Failed to resume practice session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async () => {
    if (!selectedSubtopic || sessionsToday >= maxSessions) {
      return;
    }

    setLoading(true);
    
    try {
      const selectedDurationOption = durationOptions.find(opt => opt.minutes === selectedDuration);
      
      const sessionData = {
        user_id: user?.id,
        subtopic: selectedSubtopic,
        duration_minutes: selectedDuration,
        questions_count: selectedDurationOption?.questions || 15,
        grade_level: userProfile?.grade || 'Year 4'
      };

      const response = await fetch(`${backendUrl}/api/practice/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sessionData)
      });

      if (!response.ok) {
        throw new Error(`Failed to create practice session: ${response.status}`);
      }

      const result = await response.json();
      
      // Call parent component to start the actual session
      onStartSession(result);

    } catch (err) {
      console.error('Error creating practice session:', err);
      alert('Failed to create practice session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    return 'text-orange-600 bg-orange-100';
  };

  const canStartSession = selectedSubtopic && sessionsToday < maxSessions && !loading;
  const sessionsRemaining = maxSessions - sessionsToday;

  return (
    <div className="font-opensans text-text-dark bg-bg-cream min-h-screen">
      {/* Universal Header */}
      <UniversalHeader
        currentPage="practice-corner"
        onBackToDashboard={onBack}
        breadcrumbs={[
          { label: 'Dashboard', action: onBack, current: false },
          { label: 'Practice Corner', action: null, current: true }
        ]}
      />

      {/* Session Info Bar */}
      <div style={{ background: 'var(--mc-primary-lighter)', borderBottom: '1px solid var(--mc-primary-light)' }}>
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              <span style={{ color: 'var(--mc-primary)', fontWeight: 500 }}>Grade: {userProfile?.grade || 'Year 4'}</span>
            </div>
            <div className="text-right">
              <div style={{ color: 'var(--mc-primary)' }}>
                Sessions today: <span className="font-bold">{sessionsToday}/{maxSessions}</span>
              </div>
              <div style={{ color: 'var(--mc-gray-600)', fontSize: '0.75rem' }}>
                {sessionsRemaining} remaining
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Configuration Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-poppins font-bold text-primary mb-6">Create Practice Session</h2>

              {/* Resumable Session */}
              {resumableSession && (
                <div style={{ background: 'var(--mc-primary-lighter)', border: '1px solid var(--mc-primary-light)' }} className="rounded-xl p-6 mb-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-poppins font-bold mb-2" style={{ color: 'var(--mc-primary)' }}>Resume Your Session</h3>
                      <p style={{ color: 'var(--mc-gray-600)' }} className="mb-2">
                        Continue your <strong>{resumableSession.subtopic}</strong> practice session
                      </p>
                      <div className="text-sm" style={{ color: 'var(--mc-gray-600)' }}>
                        Progress: Question {(resumableSession.current_question_index || 0) + 1} of {resumableSession.questions_count}
                        {resumableSession.time_spent_seconds && (
                          <span> • {Math.round(resumableSession.time_spent_seconds / 60)} minutes spent</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleResumeSession}
                      disabled={loading}
                      className="bg-primary hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
                    >
                      {loading ? 'Loading...' : 'Resume Session'}
                    </button>
                  </div>
                </div>
              )}

              {/* Session Limit Warning */}
              {sessionsToday >= maxSessions ? (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6 text-center">
                  <div className="text-orange-500 text-4xl mb-3">⏰</div>
                  <h3 className="text-lg font-poppins font-bold text-orange-800 mb-2">Daily Limit Reached</h3>
                  <p className="text-orange-700 mb-4">
                    You've completed {maxSessions} practice sessions today. Come back tomorrow for more practice!
                  </p>
                  <div className="text-sm text-orange-600">
                    Daily limit helps maintain effective learning pace and prevents burnout.
                  </div>
                </div>
              ) : (
                <>
                  {/* Subtopic Selection */}
                  <div className="mb-6">
                    <h3 className="text-lg font-poppins font-semibold text-primary mb-3">Choose a Subtopic</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {subtopics.map((subtopic, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedSubtopic(subtopic)}
                          className={`p-4 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-md transform hover:scale-105 ${
                            selectedSubtopic === subtopic
                              ? 'border-primary text-primary'
                              : 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'
                          }`}
                          style={selectedSubtopic === subtopic ? { background: 'var(--mc-primary-lighter)' } : {}}
                        >
                          <div className="font-medium">{subtopic}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Duration Selection */}
                  <div className="mb-6">
                    <h3 className="text-lg font-poppins font-semibold text-primary mb-3">Select Duration</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {durationOptions.map((option) => (
                        <button
                          key={option.minutes}
                          onClick={() => setSelectedDuration(option.minutes)}
                          className={`p-4 rounded-lg border-2 text-center transition-all duration-200 hover:shadow-md transform hover:scale-105 ${
                            selectedDuration === option.minutes
                              ? 'border-primary text-primary'
                              : 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'
                          }`}
                          style={selectedDuration === option.minutes ? { background: 'var(--mc-primary-lighter)' } : {}}
                        >
                          <div className="text-2xl mb-2">{option.icon}</div>
                          <div className="font-bold text-lg">{option.label}</div>
                          <div className="text-sm opacity-75 mt-1">{option.questions} questions</div>
                          <div className="text-xs opacity-60 mt-1">{option.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Start Button */}
                  <div className="text-center">
                    <button
                      onClick={handleStartSession}
                      disabled={!canStartSession}
                      className={`px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                        canStartSession
                          ? 'bg-primary hover:bg-blue-800 text-white shadow-lg'
                          : 'bg-gray-300 cursor-not-allowed text-gray-500'
                      }`}
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
                          Generating Questions...
                        </div>
                      ) : (
                        `Start ${selectedDuration} Min Practice`
                      )}
                    </button>

                    {selectedSubtopic && (
                      <div className="mt-3 text-sm text-gray-600">
                        Selected: <span className="font-medium text-primary">{selectedSubtopic}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Recent Sessions Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-poppins font-bold text-primary">Recent Sessions</h3>
                {recentSessions.length > 5 && (
                  <button 
                    onClick={async () => {
                      if (!showAllSessions) {
                        // Fetch more sessions when expanding
                        await fetchRecentSessions(25);
                      }
                      setShowAllSessions(!showAllSessions);
                    }}
                    className="text-primary hover:text-blue-800 text-sm font-medium"
                  >
                    {showAllSessions ? 'Show Less' : `View All`}
                  </button>
                )}
              </div>

              {recentSessions.length === 0 ? (
                <div className="text-center py-6">
                  <div className="text-gray-400 text-3xl mb-3">📊</div>
                  <p className="text-gray-600 text-sm">No practice sessions yet. Start your first session!</p>
                </div>
              ) : (
                <div className={`space-y-3 ${showAllSessions ? 'max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100' : ''}`}>
                  {(showAllSessions ? recentSessions : recentSessions.slice(0, 5)).map((session, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 text-sm">{session.subtopic}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(session.score)}`}>
                          {session.score}%
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{session.duration_minutes} min</span>
                        <span>{formatDate(session.created_at)}</span>
                      </div>

                      <div className="mt-2 bg-gray-100 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            session.score >= 80 ? 'bg-green-500' : 
                            session.score >= 60 ? 'bg-blue-500' : 'bg-orange-500'
                          }`}
                          style={{ width: `${session.score}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                
                {/* Show session summary when viewing all */}
                {showAllSessions && recentSessions.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="flex justify-between">
                        <span>Total Sessions:</span>
                        <span className="font-medium">{recentSessions.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Average Score:</span>
                        <span className="font-medium">
                          {Math.round(recentSessions.reduce((sum, s) => sum + s.score, 0) / recentSessions.length)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Best Score:</span>
                        <span className="font-medium text-green-600">
                          {Math.max(...recentSessions.map(s => s.score))}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Daily Progress */}
              <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--mc-primary-lighter)' }}>
                <h4 className="font-poppins font-medium text-primary mb-2">Today's Progress</h4>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Sessions completed</span>
                  <span className="font-bold text-primary">{sessionsToday}/{maxSessions}</span>
                </div>

                <div className="bg-white rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${(sessionsToday / maxSessions) * 100}%`, background: 'var(--mc-primary)' }}
                  ></div>
                </div>

                <div className="mt-2 text-xs text-gray-600">
                  {sessionsRemaining > 0
                    ? `${sessionsRemaining} session${sessionsRemaining > 1 ? 's' : ''} remaining today`
                    : 'Daily limit reached! Great work!'
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeSessionLauncher;